import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }

import { computed, ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { KitUpdateDocument, KitType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { formatDate, formatDateStringToIso } from '@/utils/date-utils';

// --- Component properties ---

export default /*@__PURE__*/_defineComponent({
  __name: 'KitUpdate',
  props: {
    kit: {},
    show: { type: Boolean }
  },
  emits: ['close', 'closeAndRefresh'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// --- Component emits ---
const emits = __emit;

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const newKitName = ref<string | null>(props.kit.name || null);
const newSampleCount = ref<number | null>(props.kit.sampleCount || null);
const newManufacturedAt = ref<Date | null>(props.kit.manufacturedAt);
const clearManufacturingDate = ref<boolean>(props.kit.manufacturedAt !== null);
const newExpirationMonths = ref<number | null>(
  props.kit.expirationMonths || null
);
const newDescription = ref<string | null>(props.kit.description || null);
const newVersion = ref<string | null>(props.kit.version || null);
const newBarcode = ref<string | null>(props.kit.barcode || null);
const newUmiLength = ref<number>(props.kit.umiLength || 8);

// --- Mutation ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(KitUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.kitUpdate) {
    console.debug('Kit updated:', res.data.kitUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Kit update failed: ' + res.data;
    console.error('Kit update failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error updating kit:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function close() {
  emits('close');
}

function updateRequested() {
  console.debug('Update requested');

  if (!props.kit.id) {
    mutationError.value = 'Kit ID is missing';
    console.error('Kit ID is missing');
    return;
  }

  let manufacturingDate: string | null = null;

  if (!clearManufacturingDate.value) {
    manufacturingDate = formatDateStringToIso(newManufacturedAt.value);
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.kit.id,
    sampleCount: newSampleCount.value,
    name: newKitName.value,
    description: newDescription.value,
    version: newVersion.value,
    barcode: newBarcode.value,
    umiLength: newUmiLength.value,
    manufacturedAt: manufacturingDate,
    clearManufacturingDate: clearManufacturingDate.value,
    expirationMonths: newExpirationMonths.value,
  });
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BModal), {
      modelValue: show.value,
      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((show).value = $event)),
      onHide: close
    }, {
      title: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: ['fas', 'cube'],
          class: "me-2"
        }),
        _createTextVNode(" " + _toDisplayString(__props.kit.name) + " kit ", 1)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          onClick: close,
          variant: "secondary"
        }, {
          default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
            _createTextVNode(" Close ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_unref(BButton), {
          onClick: updateRequested,
          disabled: mutationInProgress.value || mutationShowSuccess.value,
          variant: "primary"
        }, {
          default: _withCtx(() => [
            (mutationInProgress.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                  _cache[27] || (_cache[27] = _createTextVNode(" Updating Kit ", -1)),
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    label: ""
                  })
                ]))
              : (mutationShowSuccess.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                    _cache[28] || (_cache[28] = _createTextVNode(" Kit Updated ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fa', 'check-double'],
                      class: "me-2"
                    })
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_3, " Update Kit "))
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _cache[24] || (_cache[24] = _createElementVNode("h5", null, "Current state", -1)),
          _createElementVNode("p", null, [
            _cache[12] || (_cache[12] = _createElementVNode("strong", null, "Samples: ", -1)),
            _createTextVNode(" " + _toDisplayString(__props.kit.sampleCount) + " samples", 1),
            _cache[13] || (_cache[13] = _createElementVNode("br", null, null, -1)),
            _cache[14] || (_cache[14] = _createElementVNode("strong", null, "Barcode: ", -1)),
            _createTextVNode(" " + _toDisplayString(__props.kit.barcode), 1),
            _cache[15] || (_cache[15] = _createElementVNode("br", null, null, -1)),
            _cache[16] || (_cache[16] = _createElementVNode("strong", null, "Manufactured: ", -1)),
            _createTextVNode(" " + _toDisplayString(_unref(formatDate)(__props.kit.manufacturedAt, 'not specified')), 1),
            _cache[17] || (_cache[17] = _createElementVNode("br", null, null, -1)),
            _cache[18] || (_cache[18] = _createElementVNode("strong", null, "Expiration: ", -1)),
            _createTextVNode(" " + _toDisplayString(__props.kit.expirationMonths) + " months", 1),
            _cache[19] || (_cache[19] = _createElementVNode("br", null, null, -1)),
            _cache[20] || (_cache[20] = _createElementVNode("strong", null, "Description: ", -1)),
            _createTextVNode(_toDisplayString(__props.kit.description), 1),
            _cache[21] || (_cache[21] = _createElementVNode("br", null, null, -1)),
            _cache[22] || (_cache[22] = _createElementVNode("strong", null, "Version: ", -1)),
            _createTextVNode(" " + _toDisplayString(__props.kit.version ? __props.kit.version : 'not specified'), 1)
          ]),
          _cache[25] || (_cache[25] = _createElementVNode("h5", null, "Update kit", -1)),
          _createVNode(_unref(BFormGroup), {
            label: "Kit name",
            "label-for": "kit-name",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newKitName.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newKitName).value = $event)),
                id: "kit-name",
                name: "kit-name",
                placeholder: "Enter the kit name"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Kit description",
            "label-for": "kit-description",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormTextarea), {
                modelValue: newDescription.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newDescription).value = $event)),
                id: "kit-description",
                name: "kit-description",
                placeholder: "Enter a new kit description"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Kit version",
            "label-for": "kit-version",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newVersion.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newVersion).value = $event)),
                id: "kit-version",
                name: "kit-version",
                placeholder: "Enter the kit version"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Number of samples",
            "label-for": "sample-count",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newSampleCount.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newSampleCount).value = $event)),
                modelModifiers: { number: true },
                id: "sample-count",
                name: "sample-count",
                type: "number",
                placeholder: "Enter the number of samples",
                min: "0"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Kit barcode",
            "label-for": "kit-barcode",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newBarcode.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((newBarcode).value = $event)),
                id: "kit-barcode",
                name: "kit-barcode",
                placeholder: "Enter the barcode"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "UMI length",
            "label-for": "umi-length",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newUmiLength.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((newUmiLength).value = $event)),
                modelModifiers: { number: true },
                id: "umi-length",
                name: "umi-length",
                placeholder: "Enter UMI length",
                type: "number"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Manufacturing date",
            "label-for": "manufacturing-date",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newManufacturedAt.value,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((newManufacturedAt).value = $event)),
                id: "manufacturing-date",
                name: "manufacturing-date",
                placeholder: "Enter a new expiration date",
                type: "date",
                disabled: clearManufacturingDate.value
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormCheckbox), {
                modelValue: clearManufacturingDate.value,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((clearManufacturingDate).value = $event)),
                id: "clear-manufacturing-date",
                name: "clear-manufacturing-date"
              }, {
                default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                  _createTextVNode(" Clear manufacturing date ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Expiration months",
            "label-for": "expiration-months",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: newExpirationMonths.value,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((newExpirationMonths).value = $event)),
                modelModifiers: { number: true },
                id: "expiration-months",
                name: "expiration-months",
                type: "number",
                placeholder: "Enter the number of expiration months",
                min: "0"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BAlert), {
            modelValue: mutationError.value,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((mutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3",
            onDismissed: _cache[10] || (_cache[10] = ($event: any) => (mutationError.value = null))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(mutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})